import {EducationsResource, PhotoResource} from '@/common/api/api-raw-types';
import {Description, Education} from '@/profile-creation-wizard/components/for-completion/domain/Profile';
import {ProfileCompletionRepository} from '@/profile-creation-wizard/components/for-completion/domain/ProfileCompletionRepository';
import {ReasonForJoining, SeniorityLevel} from '@/profile-creation-wizard/components/for-completion/domain/types';
import {toDescription} from '@/profile-creation-wizard/components/for-completion/infra/mapper/resource/DescriptionResourceMapper';
import {toEducation} from '@/profile-creation-wizard/components/for-completion/infra/mapper/resource/EducationResourceMapper';
import {Photo} from '@malt/asset-utils';
import {urlService} from 'vue3-starter/dist/http/UrlService';
import {context} from 'vue3-starter/src/typescript/app-context/AppContext';
import {HttpServiceResponse} from 'vue3-starter/src/typescript/http/http';
import {toPhoto} from '../mapper/resource/PhotoResourceMapper';

export class ProfileCompletionRepositoryAPI implements ProfileCompletionRepository {
    async fetchDescription(): Promise<Description> {
        const [data, error] = await context.httpService.get<Description>(context.registry.entries.description());
        if (error) {
            throw new Error(error.message);
        }

        return toDescription(data);
    }

    async fetchEducations(): Promise<Education[]> {
        const [data, error] = await context.httpService.get<EducationsResource>(context.registry.entries.getEducations());
        if (error) {
            throw new Error(error.message);
        }

        const educations: Education[] = [];
        if (data && data.educations && data.educations.length > 0) {
            data.educations.forEach((educationResource) => {
                educations.push(toEducation(educationResource));
            });
        }

        return educations;
    }

    async fetchPhoto(profileId: string): Promise<Photo | null> {
        const entry = `${urlService.buildUrl(context.registry.entries.getPhoto(), profileId)}`;

        const [data, error] = await context.httpService.get<PhotoResource>(entry);
        if (error) {
            throw new Error(error.message);
        }

        return data ? toPhoto(data) : null;
    }

    async importResumeFromLinkedIn(linkedinResume: File): Promise<void> {
        const formData = new FormData();
        formData.append('resume', linkedinResume);

        const [data, error] = await context.httpService.post(context.registry.entries.uploadResume(), formData);
        if (error) {
            throw new Error(error.message);
        }
    }

    async sendRecommendationRequests(emails: string, message: string): Promise<HttpServiceResponse<any>> {
        return await context.httpService.post(context.registry.entries.recommendationRequests(), {emails, message});
    }

    async updateDescription(description: string): Promise<HttpServiceResponse<void>> {
        return await context.httpService.put(context.registry.entries.description(), {description});
    }

    async updateProfilePreference(indexable: boolean): Promise<HttpServiceResponse<any>> {
        return await context.httpService.post(context.registry.entries.preferences(), {indexable});
    }

    async updateReasonsForJoining(reasons: Array<ReasonForJoining>): Promise<HttpServiceResponse<any>> {
        return await context.httpService.post(context.registry.entries.reasonsForJoining(), {reasons});
    }

    async updateSeniorityLevel(seniority: SeniorityLevel): Promise<HttpServiceResponse<any>> {
        return await context.httpService.post(context.registry.entries.seniority(), {seniority});
    }
}
